import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, Component } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { WishlistProvider } from './context/WishlistContext';
import { CompareProvider } from './context/CompareContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '934315483691-cj7ptslpr4h0fp9il9ttba6rs22bftvo.apps.googleusercontent.com';

// Public pages
import Home from './pages/public/Home';
import Products from './pages/public/Products';
import ProductDetail from './pages/public/ProductDetail';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import CustomerLogin from './pages/public/CustomerLogin';
import CustomerRegister from './pages/public/CustomerRegister';
import VerifyEmail from './pages/public/VerifyEmail';
import Account from './pages/public/Account';
import CompleteProfile from './pages/public/CompleteProfile';
import Checkout from './pages/public/Checkout';
import PaymentResult from './pages/public/PaymentResult';
import LegalPage from './pages/public/LegalPage';
import ForgotPassword from './pages/public/ForgotPassword';
import Favorites from './pages/public/Favorites';
import Wishlist from './pages/public/Wishlist';
import Compare from './pages/public/Compare';

// Admin pages
import Login from './pages/admin/Login';
import ProductsAdmin from './pages/admin/ProductsAdmin';
import CategoriesAdmin from './pages/admin/CategoriesAdmin';
import SliderAdmin from './pages/admin/SliderAdmin';
import ReviewsAdmin from './pages/admin/ReviewsAdmin';
import QuestionsAdmin from './pages/admin/QuestionsAdmin';
import SiteSettingsAdmin from './pages/admin/SiteSettingsAdmin';
import UsersAdmin from './pages/admin/UsersAdmin';
import CustomersAdmin from './pages/admin/CustomersAdmin';
import MediaLibraryAdmin from './pages/admin/MediaLibraryAdmin';
import PromoBannerAdmin from './pages/admin/PromoBannerAdmin';
import OrdersAdmin from './pages/admin/OrdersAdmin';
import LegalPagesAdmin from './pages/admin/LegalPagesAdmin';
import CouponsAdmin from './pages/admin/CouponsAdmin';
import BlogAdmin from './pages/admin/BlogAdmin';
import BlogCategoriesAdmin from './pages/admin/BlogCategoriesAdmin';
import ServicesAdmin from './pages/admin/ServicesAdmin';
import CorporatePagesAdmin from './pages/admin/CorporatePagesAdmin';
import PageSeoAdmin from './pages/admin/PageSeoAdmin';
import HomepageCategoriesAdmin from './pages/admin/HomepageCategoriesAdmin';
import EditorialBannerAdmin from './pages/admin/EditorialBannerAdmin';
import PopupBannerAdmin from './pages/admin/PopupBannerAdmin';

// Layout
import Navbar, { WhatsAppFloatingButton } from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import AdminLayout from './components/layout/AdminLayout';
import CartSidebar from './components/cart/CartSidebar';
import PopupBanner from './components/PopupBanner';
import CompareBar from './components/CompareBar';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('React Error:', error, info); }
  render() {
    if (this.state.error) {
      return <div style={{padding:'2rem',color:'red',fontFamily:'monospace'}}>
        <h2>Hata olustu:</h2>
        <pre style={{whiteSpace:'pre-wrap'}}>{this.state.error.message}</pre>
        <pre style={{whiteSpace:'pre-wrap',fontSize:'0.8rem',color:'#666'}}>{this.state.error.stack}</pre>
      </div>;
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!user || user.mustChangePassword) return <Navigate to="/admin/login" />;
  return children;
};

function PublicPage({ children }) {
  return <><Navbar />{children}<CartSidebar /><CompareBar /><PopupBanner /><WhatsAppFloatingButton /><Footer /></>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicPage><Home /></PublicPage>} />
      <Route path="/urunler" element={<PublicPage><Products /></PublicPage>} />
      <Route path="/urun/:slug" element={<PublicPage><ProductDetail /></PublicPage>} />
      <Route path="/hakkimizda" element={<PublicPage><About /></PublicPage>} />
      <Route path="/iletisim" element={<PublicPage><Contact /></PublicPage>} />
      <Route path="/giris" element={<PublicPage><CustomerLogin /></PublicPage>} />
      <Route path="/kayit" element={<PublicPage><CustomerRegister /></PublicPage>} />
      <Route path="/sifremi-unuttum" element={<PublicPage><ForgotPassword /></PublicPage>} />
      <Route path="/eposta-dogrula" element={<PublicPage><VerifyEmail /></PublicPage>} />
      <Route path="/profil-tamamla" element={<PublicPage><CompleteProfile /></PublicPage>} />
      <Route path="/favoriler" element={<PublicPage><Favorites /></PublicPage>} />
      <Route path="/istek-listem" element={<PublicPage><Wishlist /></PublicPage>} />
      <Route path="/karsilastir" element={<PublicPage><Compare /></PublicPage>} />
      <Route path="/hesabim" element={<PublicPage><Account /></PublicPage>} />
      <Route path="/checkout" element={<><Navbar /><Checkout /><CartSidebar /></>} />
      <Route path="/odeme-sonucu" element={<PublicPage><PaymentResult /></PublicPage>} />
      <Route path="/sayfa/:slug" element={<PublicPage><LegalPage /></PublicPage>} />

      {/* Admin */}
      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/products" replace />} />
        <Route path="products" element={<ProductsAdmin />} />
        <Route path="categories" element={<CategoriesAdmin />} />
        <Route path="orders" element={<OrdersAdmin />} />
        <Route path="sliders" element={<SliderAdmin />} />
        <Route path="banners" element={<PromoBannerAdmin />} />
        <Route path="media-library" element={<MediaLibraryAdmin />} />
        <Route path="reviews" element={<ReviewsAdmin />} />
        <Route path="questions" element={<QuestionsAdmin />} />
        <Route path="site-settings" element={<SiteSettingsAdmin />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="customers" element={<CustomersAdmin />} />
        <Route path="legal-pages" element={<LegalPagesAdmin />} />
        <Route path="coupons" element={<CouponsAdmin />} />
        <Route path="blogs" element={<BlogAdmin />} />
        <Route path="blog-categories" element={<BlogCategoriesAdmin />} />
        <Route path="services" element={<ServicesAdmin />} />
        <Route path="corporate-pages" element={<CorporatePagesAdmin />} />
        <Route path="page-seo" element={<PageSeoAdmin />} />
        <Route path="homepage-categories" element={<HomepageCategoriesAdmin />} />
        <Route path="editorial-banner" element={<EditorialBannerAdmin />} />
        <Route path="popup-banner" element={<PopupBannerAdmin />} />
      </Route>
    </Routes>
  );
}

function App({ onReady }) {
  const called = useRef(false);
  useEffect(() => {
    if (!called.current) {
      called.current = true;
      setTimeout(() => onReady?.(), 2800);
    }
  }, [onReady]);

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <SiteSettingsProvider>
            <CustomerAuthProvider>
              <CartProvider>
                <Router>
                  <FavoritesProvider>
                    <WishlistProvider>
                      <CompareProvider>
                        <ScrollToTop />
                        <AppRoutes />
                      </CompareProvider>
                    </WishlistProvider>
                  </FavoritesProvider>
                </Router>
              </CartProvider>
            </CustomerAuthProvider>
          </SiteSettingsProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
